import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  getServiceCategoryMasterEdit,
  storeServiceCategoryMaster,
  updateServiceCategoryMaster
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const FreightServiceCategoriesFormPage = () => {
  const navigate = useNavigate();
  const newMatch = useMatch({ path: "/freight/service_categories/new", end: true });
  const { id: routeId } = useParams();
  const isNew = Boolean(newMatch);
  const masterId = isNew ? null : routeId;

  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Service categories", link: "/freight/service_categories" },
      { label: isNew ? "New category" : `Edit #${masterId ?? ""}` }
    ],
    [roleLabel, isNew, masterId]
  );

  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [status, setStatus] = useState("1");

  const loadEdit = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const res = await getServiceCategoryMasterEdit(masterId);
      const d = res?.data?.serviceCategoryData ?? res?.data?.serviceCategory ?? res?.data;
      if (!d) throw new Error("Not found");
      setName(String(d.name ?? ""));
      setOrderNo(d.order_no != null ? String(d.order_no) : "");
      setStatus(String(d.status ?? "1"));
    } catch (e) {
      showError(e.message || "Load failed");
      navigate("/freight/service_categories", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [masterId, navigate]);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    loadEdit();
  }, [isNew, loadEdit]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      showError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: trimmed,
        order_no: orderNo === "" ? undefined : Number(orderNo),
        status: Number(status)
      };
      if (isNew) {
        await storeServiceCategoryMaster(payload);
        showSuccess("Service category created");
      } else {
        await updateServiceCategoryMaster({
          id: Number(masterId),
          ...payload
        });
        showSuccess("Service category updated");
      }
      navigate("/freight/service_categories");
    } catch (err) {
      showError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumbs={breadcrumbs}>
        <div className="max-w-[720px] card offers-table p-6">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[720px]">
        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="m-0">{isNew ? "New service category" : "Edit service category"}</h3>
            <Link to="/freight/service_categories" className="btn-outline no-underline text-sm">
              ← Back to list
            </Link>
          </div>

          <form className="freight-admin-form space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required maxLength={255} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display order</label>
              <input type="number" min={0} value={orderNo} onChange={(e) => setOrderNo(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving…" : isNew ? "Create" : "Save changes"}
              </button>
              <Link to="/freight/service_categories" className="btn-outline no-underline inline-flex items-center">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightServiceCategoriesFormPage;
